import {
  ContentCopy as CopyIcon,
  Delete as DeleteIcon,
  Link as LinkIcon,
  Lock as LockIcon,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { appUrl } from "../../common/api/config";
import {
  createSharedLink,
  getSharedLinks,
  revokeSharedLink,
} from "../api/alerts";

export default function SharedReports() {
  const { t } = useTranslation("alerts");
  const [links, setLinks] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [form, setForm] = useState({
    report: "",
    asset: "",
    period_start: "",
    period_end: "",
    expires_in_days: 30,
    password: "",
    max_views: "",
  });

  useEffect(() => {
    loadLinks();
  }, []);

  const loadLinks = async () => {
    try {
      setLinks(await getSharedLinks());
    } catch {
      /* ignore */
    }
  };

  const showSnack = (message, severity = "success") =>
    setSnackbar({ open: true, message, severity });

  const handleCreate = async () => {
    try {
      const data = {
        expires_in_days: form.expires_in_days,
        ...(form.report && { report: parseInt(form.report) }),
        ...(form.asset && { asset: parseInt(form.asset) }),
        ...(form.period_start && { period_start: form.period_start }),
        ...(form.period_end && { period_end: form.period_end }),
        ...(form.password && { password: form.password }),
        ...(form.max_views && { max_views: parseInt(form.max_views) }),
      };
      await createSharedLink(data);
      setDialogOpen(false);
      loadLinks();
      showSnack(t("common.success"));
    } catch (e) {
      showSnack(e.message, "error");
    }
  };

  const handleRevoke = async (id) => {
    if (!window.confirm(t("sharing.revokeConfirm"))) return;
    try {
      await revokeSharedLink(id);
      loadLinks();
      showSnack(t("common.success"));
    } catch (e) {
      showSnack(e.message, "error");
    }
  };

  const handleCopy = (token) => {
    const url = `${appUrl}/shared-report/${token}`;
    navigator.clipboard.writeText(url);
    showSnack(t("sharing.linkCopied"));
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Box>
          <Typography variant="h4" sx={{ mb: 0.5 }}>
            {t("sharing.title")}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t("sharing.description")}
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<LinkIcon />}
          onClick={() => setDialogOpen(true)}
        >
          {t("sharing.createLink")}
        </Button>
      </Box>

      {links.length === 0 ? (
        <Typography color="text.secondary">{t("sharing.noLinks")}</Typography>
      ) : (
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t("sharing.token")}</TableCell>
              <TableCell>{t("sharing.expires")}</TableCell>
              <TableCell>{t("sharing.views")}</TableCell>
              <TableCell>{t("sharing.passwordProtected")}</TableCell>
              <TableCell>{t("common.actions")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {links.map((link) => (
              <TableRow key={link.id}>
                <TableCell>
                  <Typography
                    variant="body2"
                    sx={{ fontFamily: "monospace", fontSize: 12 }}
                  >
                    {link.token.substring(0, 8)}...
                  </Typography>
                  {link.report_filename && (
                    <Typography variant="caption" color="text.secondary">
                      {link.report_filename}
                    </Typography>
                  )}
                  {link.asset_title && (
                    <Typography variant="caption" color="text.secondary">
                      {link.asset_title}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  {new Date(link.expires_at).toLocaleDateString()}
                  {link.is_expired && (
                    <Chip
                      label="Expired"
                      size="small"
                      color="error"
                      sx={{ ml: 1 }}
                    />
                  )}
                </TableCell>
                <TableCell>
                  {link.view_count}
                  {link.max_views ? ` / ${link.max_views}` : ""}
                </TableCell>
                <TableCell>
                  {link.is_password_protected ? (
                    <LockIcon fontSize="small" color="warning" />
                  ) : (
                    "-"
                  )}
                </TableCell>
                <TableCell>
                  <Tooltip title={t("sharing.copyLink")}>
                    <IconButton
                      size="small"
                      onClick={() => handleCopy(link.token)}
                    >
                      <CopyIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={t("sharing.revoke")}>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleRevoke(link.id)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {/* Create Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t("sharing.createLink")}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            type="number"
            label={t("sharing.report")}
            value={form.report}
            onChange={(e) => setForm({ ...form, report: e.target.value })}
            sx={{ mt: 2, mb: 2 }}
            helperText="Report ID"
          />
          <TextField
            fullWidth
            type="number"
            label={t("sharing.asset")}
            value={form.asset}
            onChange={(e) => setForm({ ...form, asset: e.target.value })}
            sx={{ mb: 2 }}
            helperText="Asset ID"
          />
          <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
            <TextField
              fullWidth
              type="date"
              label={t("sharing.periodStart")}
              value={form.period_start}
              onChange={(e) =>
                setForm({ ...form, period_start: e.target.value })
              }
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              fullWidth
              type="date"
              label={t("sharing.periodEnd")}
              value={form.period_end}
              onChange={(e) => setForm({ ...form, period_end: e.target.value })}
              InputLabelProps={{ shrink: true }}
            />
          </Box>
          <TextField
            fullWidth
            type="number"
            label={t("sharing.expiresInDays")}
            value={form.expires_in_days}
            onChange={(e) =>
              setForm({
                ...form,
                expires_in_days: parseInt(e.target.value) || 30,
              })
            }
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            type="password"
            label={t("sharing.password")}
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            type="number"
            label={t("sharing.maxViews")}
            value={form.max_views}
            onChange={(e) => setForm({ ...form, max_views: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>
            {t("common.cancel")}
          </Button>
          <Button variant="contained" onClick={handleCreate}>
            {t("common.create")}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
